import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, X } from "lucide-react";
import TopBar from "@/components/TopBar";
import { Accounts } from "@/api/endpoints";
import { apiErrorMessage } from "@/api/client";
import { useAuthStore } from "@/store/auth";
import { confirmDialog, toast } from "@/lib/feedback";

export default function AccountsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [showNew, setShowNew] = useState(false);
  const isAdmin = useAuthStore((s) => s.user)?.role === "admin";

  const accountsQuery = useQuery({
    queryKey: ["accounts", q],
    queryFn: () => Accounts.list({ q: q || undefined }),
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => Accounts.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["accounts"] }),
    onError: (e) => toast.err(`Delete failed: ${apiErrorMessage(e)}`),
  });

  const accounts = accountsQuery.data ?? [];

  return (
    <>
      <TopBar
        title="Accounts"
        onSearch={setQ}
        searchPlaceholder="Filter accounts…"
        actions={
          isAdmin && (
            <button className="btn btn-primary" onClick={() => setShowNew(true)}>
              <Plus className="w-3.5 h-3.5" /> New account
            </button>
          )
        }
      />

      <div className="px-4 py-4 md:px-6 md:py-6 space-y-4">
        <div className="panel overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-fg/[0.02] border-b border-fg/[0.05]">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-mut-300">
                  Name
                </th>
                <th className="hidden sm:table-cell px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-mut-300">
                  Notes
                </th>
                <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wider text-mut-300">
                  Deals
                </th>
                {isAdmin && <th className="px-3 py-2" />}
              </tr>
            </thead>
            <tbody>
              {accounts.map((a) => (
                <tr
                  key={a.id}
                  className="border-t border-fg/[0.04] hover:bg-fg/[0.02] cursor-pointer"
                  onClick={() => navigate("/accounts/" + a.id)}
                >
                  <td className="px-3 py-2.5 font-medium">
                    {a.name}
                    {a.notes && (
                      <div className="sm:hidden mt-0.5 line-clamp-2 text-xs font-normal text-mut-500">
                        {a.notes}
                      </div>
                    )}
                  </td>
                  <td className="hidden sm:table-cell px-3 py-2.5 text-mut-300 truncate max-w-[420px]">
                    {a.notes || "—"}
                  </td>
                  <td className="px-3 py-2.5 text-right">{a.deal_count}</td>
                  {isAdmin && (
                    <td className="px-3 py-2.5 text-right">
                      <button
                        className="btn btn-ghost text-accent-pink"
                        title="Delete account"
                        aria-label={`Delete ${a.name}`}
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (
                            await confirmDialog({
                              title: `Delete ${a.name}?`,
                              body: "Its deals stay, but they'll no longer belong to an account.",
                              confirmLabel: "Delete account",
                              danger: true,
                            })
                          ) {
                            removeMutation.mutate(a.id);
                          }
                        }}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
              {accounts.length === 0 && (
                <tr>
                  <td colSpan={isAdmin ? 4 : 3} className="px-3 py-10 text-center text-mut-300">
                    {accountsQuery.isLoading ? "Loading…" : "No accounts match."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showNew && <NewAccountModal onClose={() => setShowNew(false)} />}
    </>
  );
}

function NewAccountModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => Accounts.create({ name: name.trim(), notes: notes.trim() || null }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["accounts"] });
      onClose();
    },
    onError: (e) => setError(apiErrorMessage(e)),
  });

  return (
    <>
      <div className="overlay-backdrop z-40" onClick={onClose} />
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[min(480px,92vw)] panel p-5 max-h-[90dvh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">New account</h2>
          <button className="btn btn-ghost" onClick={onClose}>
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="label">Name*</label>
            <input
              className="input"
              autoFocus
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setError(null);
              }}
            />
          </div>
          <div>
            <label className="label">Notes</label>
            <textarea
              className="input min-h-[80px]"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
          {error && <div className="text-sm text-accent-pink">{error}</div>}
          <div className="flex justify-end gap-2 pt-1">
            <button className="btn" onClick={onClose}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              disabled={!name.trim() || mutation.isPending}
              onClick={() => mutation.mutate()}
            >
              {mutation.isPending ? "Creating…" : "Create account"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
